const getQuickViewButton = (target) => {
  const quickView = target.closest('.jozee-fs-quick-view');
  if (!quickView) return null;
  return quickView.querySelector('button.quick-add__submit, .jozee-fs-quick-view-button');
};

const waitForCustomElement = (name) =>
  customElements.get(name)
    ? Promise.resolve()
    : Promise.race([
        customElements.whenDefined(name),
        new Promise((resolve) => setTimeout(resolve, 2500)),
      ]);

const openQuickViewModal = async (modalSelector, button) => {
  await waitForCustomElement('quick-add-modal');

  const modal = document.querySelector(modalSelector);
  if (!modal || typeof modal.show !== 'function') return;

  modal.show(button);
};

const handleQuickViewAction = (event) => {
  const button = getQuickViewButton(event.target);
  if (!button) return;

  event.preventDefault();
  event.stopImmediatePropagation();
  event.stopPropagation();

  const opener = button.closest('modal-opener');
  if (!opener) return;

  const modalSelector = opener.getAttribute('data-modal');
  if (!modalSelector) return;

  openQuickViewModal(modalSelector, button);
};

document.addEventListener(
  'click',
  handleQuickViewAction,
  { capture: true }
);

document.addEventListener('keydown', (event) => {
  if (event.key !== 'Enter' && event.key !== ' ') return;
  handleQuickViewAction(event);
});

document.addEventListener(
  'click',
  (event) => {
    const link = event.target.closest('.jozee-fs-main-nav a[href], .menu-drawer a[href]');
    if (!link) return;

    const destination = new URL(link.getAttribute('href'), window.location.href);
    if (destination.protocol !== 'http:' && destination.protocol !== 'https:') return;

    const current = new URL(window.location.href);
    if (destination.pathname === current.pathname && destination.search === current.search) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.assign(destination.href);
  },
  { capture: true }
);

(function initMainNavDropdowns() {
  const nav = document.querySelector('.jozee-fs-main-nav');
  if (!nav) return;

  const DROPDOWN_SELECTOR = '.jozee-fs-main-nav__item--has-dropdown';
  const items = nav.querySelectorAll(DROPDOWN_SELECTOR);
  if (!items.length) return;

  items.forEach((item) => {
    const dropdown = item.querySelector('.jozee-fs-main-nav__dropdown');
    if (!dropdown) return;

    let hideTimer;
    const openDropdown = (event) => {
      if (hideTimer) {
        clearTimeout(hideTimer);
        hideTimer = null;
      }
      item.classList.add('is-open');
      item.setAttribute('aria-expanded', 'true');
      dropdown.setAttribute('aria-hidden', 'false');
    };

    const closeDropdown = (event) => {
      if (hideTimer) clearTimeout(hideTimer);
      hideTimer = setTimeout(() => {
        item.classList.remove('is-open');
        item.removeAttribute('aria-expanded');
        dropdown.setAttribute('aria-hidden', 'true');
      }, 280);
    };

    item.addEventListener('mouseenter', openDropdown);
    item.addEventListener('mouseleave', closeDropdown);
    dropdown.addEventListener('mouseenter', openDropdown);
    dropdown.addEventListener('mouseleave', closeDropdown);

    item.addEventListener('focusin', openDropdown);
    item.addEventListener('focusout', (event) => {
      if (!item.contains(event.relatedTarget)) closeDropdown();
    });
  });
})();

(function initShopMenuTrees() {
  const trees = document.querySelectorAll('[data-jozee-shop-tree]');
  if (!trees.length) return;

  trees.forEach((tree) => {
    const triggers = tree.querySelectorAll('[data-jozee-shop-tree-trigger]');
    const panels = tree.querySelectorAll('[data-jozee-shop-tree-panel]');
    if (!triggers.length || !panels.length) return;

    const isTabTree = Array.from(triggers).some((trigger) => trigger.getAttribute('role') === 'tab');

    const activateGroup = (group) => {
      triggers.forEach((trigger) => {
        const isActive = trigger.dataset.jozeeShopTreeTrigger === group;
        trigger.classList.toggle('is-active', isActive);

        if (trigger.getAttribute('role') === 'tab') {
          trigger.setAttribute('aria-selected', isActive ? 'true' : 'false');
        }

        if (trigger.hasAttribute('aria-expanded')) {
          trigger.setAttribute('aria-expanded', isActive ? 'true' : 'false');
        }
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.jozeeShopTreePanel === group;
        panel.classList.toggle('is-active', isActive);
        panel.toggleAttribute('hidden', !isActive);
      });
    };

    const toggleGroup = (group) => {
      const trigger = tree.querySelector(`[data-jozee-shop-tree-trigger="${group}"]`);
      const panel = tree.querySelector(`[data-jozee-shop-tree-panel="${group}"]`);
      if (!trigger || !panel) return;

      const isOpen = !panel.hasAttribute('hidden');
      trigger.classList.toggle('is-active', !isOpen);
      trigger.setAttribute('aria-expanded', !isOpen ? 'true' : 'false');
      panel.classList.toggle('is-active', !isOpen);
      panel.toggleAttribute('hidden', isOpen);
    };

    triggers.forEach((trigger) => {
      const group = trigger.dataset.jozeeShopTreeTrigger;

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        isTabTree ? activateGroup(group) : toggleGroup(group);
      });

      trigger.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        isTabTree ? activateGroup(group) : toggleGroup(group);
      });

      trigger.addEventListener('mouseenter', () => {
        if (!window.matchMedia('(min-width: 990px)').matches || trigger.getAttribute('role') !== 'tab') return;
        activateGroup(group);
      });
    });

    if (isTabTree) {
      activateGroup(tree.querySelector('[data-jozee-shop-tree-trigger].is-active')?.dataset.jozeeShopTreeTrigger || 'category');
    }
  });
})();

(function initZoomifiCurrencyFallback() {
  const SELECTOR = '.Pb_currency_wrapper select[name="PB_Currency_select"]';
  const PRICE_SELECTOR = '.price-item[data-jozee-base-price]';
  const FALLBACK_CURRENCIES = ['EGP', 'USD', 'GBP', 'EUR', 'AUD', 'CAD', 'AED'];
  let attempts = 0;

  const getBaseCurrency = () =>
    window.mlvShopMainCurrency || window.mlvShopCurrency || window.Shopify?.currency?.active || 'EGP';

  const formatConvertedPrice = (amountInCents, currency) => {
    const converter = window.pb_Currency_global;
    if (!converter || typeof converter.getConvertedPrice !== 'function') return null;

    const previousCurrency = converter.currentCurrency;
    converter.currentCurrency = currency;
    const formattedPrice = converter.getConvertedPrice(amountInCents);
    converter.currentCurrency = previousCurrency;

    if (!formattedPrice) return null;
    return /\b[A-Z]{3}\b/.test(formattedPrice) ? formattedPrice : `${formattedPrice} ${currency}`;
  };

  const convertVisiblePrices = (currency) => {
    const baseCurrency = getBaseCurrency();

    document.querySelectorAll(PRICE_SELECTOR).forEach((priceElement) => {
      if (!priceElement.dataset.jozeeOriginalPriceHtml) {
        priceElement.dataset.jozeeOriginalPriceHtml = priceElement.innerHTML.trim();
      }

      if (currency === baseCurrency) {
        priceElement.innerHTML = priceElement.dataset.jozeeOriginalPriceHtml;
        return;
      }

      const amountInCents = Number(priceElement.dataset.jozeeBasePrice);
      if (!Number.isFinite(amountInCents)) return;

      const convertedPrice = formatConvertedPrice(amountInCents, currency);
      if (convertedPrice) priceElement.textContent = convertedPrice;
    });
  };

  const addCurrencyOption = (select, currency, prepend = false) => {
    if (select.querySelector(`option[value="${currency}"]`)) return;
    const option = document.createElement('option');
    option.value = currency;
    option.textContent = currency;
    option.dataset.display = currency;
    prepend ? select.prepend(option) : select.append(option);
  };

  const addCurrencyOptions = (select) => {
    const baseCurrency = getBaseCurrency();
    const rates = window.pb_Currency_global?.rates || {};

    addCurrencyOption(select, baseCurrency, true);
    FALLBACK_CURRENCIES.forEach((currency) => {
      if (currency === baseCurrency || !rates[currency]) return;
      addCurrencyOption(select, currency);
    });

    return baseCurrency;
  };

  const setup = () => {
    attempts += 1;

    const select = document.querySelector(SELECTOR);
    if (!select || !window.pb_Currency_global) {
      if (attempts < 30) window.setTimeout(setup, 500);
      return;
    }

    const baseCurrency = addCurrencyOptions(select);
    if (!select.value) select.value = baseCurrency;
    convertVisiblePrices(select.value);

    select.addEventListener('change', () => {
      convertVisiblePrices(select.value || baseCurrency);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setup);
  } else {
    setup();
  }
})();
